import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Row, Col, Form } from "react-bootstrap";
import ChartCard from "../ui/CardChart";
import StatCard from "../ui/StatCard";
import { FaDollarSign } from "react-icons/fa";


const COLORS = ["#4facfe", "#667eea", "#764ba2", "#f093fb"];
const PAGE_SIZE = 15;

const OrdersAnalytics = ({ data, loading }) => {
  // -----------------------------
  // 1) SAFE DATA ACCESS
  // -----------------------------
  const currencies = data?.currencies ?? {};
  const currencyKeys = Object.keys(currencies).sort((a, b) => {
  if (a === "USD") return -1;
  if (b === "USD") return 1;
  return a.localeCompare(b);
});

  // -----------------------------
  // 2) SELECTED CURRENCY STATE
  // Hooks must come before any early returns
  // -----------------------------
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [selectedCurrency, setSelectedCurrency] = useState(
    currencyKeys[0] ?? ""
  );

  // Pagination state
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [page, setPage] = useState(1);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!currencyKeys.includes(selectedCurrency)) {
      setSelectedCurrency(currencyKeys[0] ?? "");
    }
  }, [currencyKeys, selectedCurrency]);

  // Reset page on currency change
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    setPage(1);
  }, [selectedCurrency]);

  if (!loading && currencyKeys.length === 0) {
    return <div>No order data available</div>;
  }

  // -----------------------------
  // 3) CURRENCY DATA
  // -----------------------------
  const cdata = currencies[selectedCurrency] ?? {
    chartData: [],
    counts: { deposits: 0, refunds: 0, withdrawals: 0 },
    orders: [],
  };

  // -----------------------------
  // 4) FILTER ZERO VALUES
  // -----------------------------
  const chartData = (Array.isArray(cdata.chartData)
    ? cdata.chartData
    : []
  ).filter((d) => (d?.value ?? 0) > 0);

  const orders = (Array.isArray(cdata.orders) ? cdata.orders : []).filter(
    (o) =>
      (o.scoring ?? 0) +
        (o.deposited ?? 0) +
        (o.refunded ?? 0) +
        (o.withdrawn ?? 0) >
      0
  );

  const counts = cdata.counts ?? {
    deposits: 0,
    refunds: 0,
    withdrawals: 0,
  };

  // -----------------------------
  // 5) DERIVE TOTALS
  // -----------------------------
  const getValue = (name) =>
    chartData.find((d) => d.name === name)?.value ?? 0;

  const totals = {
    scoring: getValue("Scoring"),
    deposited: getValue("Deposited"),
    refunded: getValue("Refunded"),
    withdrawn: getValue("Withdrawn"),
  };

  // -----------------------------
  // 6) PAGINATION LOGIC
  // -----------------------------
  const totalPages = Math.ceil(orders.length / PAGE_SIZE);

  const pagedOrders = orders.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // -----------------------------
  // 7) LOADING
  // -----------------------------
  if (loading) {
    return <div>Loading orders…</div>;
  }

  // -----------------------------
  // 8) RENDER
  // -----------------------------
  return (
    <>
      {/* ===================== */}
      {/*   STAT CARDS          */}
      {/* ===================== */}
      <Row className="mb-4">
        {totals.deposited > 0 && (
          <Col md={6}>
            <StatCard
              icon={FaDollarSign}
              title={`Total Deposited (${selectedCurrency})`}
              value={totals.deposited.toFixed(2)}
              subtitle={`${counts.deposits} deposits`}
              color="from-blue-500 to-blue-600"
            />
          </Col>
        )}

        {totals.refunded > 0 && (
          <Col md={6}>
            <StatCard
              icon={FaDollarSign}
              title={`Total Refunded (${selectedCurrency})`}
              value={totals.refunded.toFixed(2)}
              subtitle={`${counts.refunds} refunds`}
              color="from-pink-500 to-pink-600"
            />
          </Col>
        )}
      </Row>

      {/* ===================== */}
      {/*   CURRENCY SELECT     */}
      {/* ===================== */}
      <Row className="mb-3 mx-0">
        <Col xs={12} className="px-1">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-semibold">Currency:</span>
            <Form.Select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              style={{ width: "200px" }}
            >
              {currencyKeys.map((cur) => (
                <option key={cur} value={cur}>
                  {cur}
                </option>
              ))}
            </Form.Select>
          </div>
        </Col>
      </Row>

      {/* ===================== */}
      {/*   PIE CHART           */}
      {/* ===================== */}
      {chartData.length > 0 && (
        <Row className="mb-4 mx-0">
          <Col xs={12} className="px-1">
            <ChartCard
              title={`💰 ${selectedCurrency} Transaction Breakdown`}
              loading={loading}
            >
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={95}
                    innerRadius={50}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, value }) =>
                      `${name}: ${(value ?? 0).toFixed(2)}`
                    }
                  >
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => (v ?? 0).toFixed(2)} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      )}

      {/* ===================== */}
      {/*   ORDERS TABLE        */}
      {/* ===================== */}
      {orders.length > 0 && (
        <Row className="mx-0">
          <Col xs={12} className="px-1">
            <ChartCard
              title={`📋 Recent ${selectedCurrency} Orders`}
              loading={loading}
            >
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-3 py-2 text-left">Order</th>
                      <th className="px-3 py-2 text-right">Scoring</th>
                      <th className="px-3 py-2 text-right">Deposited</th>
                      <th className="px-3 py-2 text-right">Refunded</th>
                      <th className="px-3 py-2 text-right">Withdrawn</th>
                      <th className="px-3 py-2 text-left">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedOrders.map((order, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-left">
                          {order.order_key ?? "-"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(order.scoring ?? 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(order.deposited ?? 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(order.refunded ?? 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(order.withdrawn ?? 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-left">
                          {order.timestamp ?? "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                  <span style={{ fontSize: 12, color: "#6B7280" }}>
                    Page {page} of {totalPages}
                  </span>

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page === totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </ChartCard>
          </Col>
        </Row>
      )}
    </>
  );
};

export default OrdersAnalytics;
